/**
 * Resolves all promises, returning only the fulfilled values.
 * Optionally calls `onRejected` for each rejected promise with the reason and index.
 */
export async function allFulfilled<T>(
  promises: Promise<T>[],
  onRejected?: (reason: unknown, index: number) => void
): Promise<T[]> {
  const results = await Promise.allSettled(promises)

  const fulfilled: T[] = []
  for (const [i, result] of results.entries()) {
    if (result.status === 'fulfilled') {
      fulfilled.push(result.value)
    } else if (onRejected) {
      onRejected(result.reason, i)
    }
  }
  return fulfilled
}

/**
 * Reject with a `TimeoutError` (`DOMException`) if `promise` does not settle within
 * `timeoutMs`. The underlying operation is not cancelled — use a caller-side
 * `AbortSignal` when the operation supports one.
 */
export function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  const signal = AbortSignal.timeout(timeoutMs)
  return new Promise<T>((resolve, reject) => {
    signal.addEventListener('abort', () => reject(signal.reason), { once: true })
    promise.then(resolve, reject)
  })
}
